import { createSlice } from "@reduxjs/toolkit";
import { apiThunk } from "App/store/apiThunk";

/**
 * Agency money: balance, statement, earnings summary and withdrawals
 * (TENANT_AGENCY_MODULE.md §7).
 *
 * The agency has no wallet column. Its "balance" is the running `balance` on the
 * newest `p_transactions_party` row for the DRIVER ACCOUNT ledger against the
 * agency's `payout_cust_id` — a party's role is implied purely by which ledger
 * its row sits on, which is why paying an agency instead of a driver needs only
 * a different party id.
 */

interface AgencyArg {
  agencyId: number | string;
}

export const loadBalance = apiThunk<any, AgencyArg>("payments/balance", {
  server: "payment",
  url: ({ agencyId }) => `tenant-agencies/${agencyId}/balance`,
  method: "GET",
  errorMsg: true,
});

/** Today / week / month / all-time plus a 30-day series. */
export const loadEarningsSummary = apiThunk<any, AgencyArg>("payments/earningsSummary", {
  server: "payment",
  url: ({ agencyId }) => `tenant-agencies/${agencyId}/earnings-summary`,
  method: "GET",
});

export const loadStatement = apiThunk<any, AgencyArg & { filters?: Record<string, any> }>(
  "payments/statement",
  {
    server: "payment",
    url: ({ agencyId }) => `tenant-agencies/${agencyId}/statement`,
    method: "GET",
    params: ({ filters }) => filters,
    errorMsg: true,
  }
);

export const loadWithdrawals = apiThunk<any, AgencyArg>("payments/withdrawals", {
  server: "payment",
  url: ({ agencyId }) => `tenant-agencies/${agencyId}/withdrawals`,
  method: "GET",
  errorMsg: true,
});

/**
 * Submitting a withdrawal has its own action so a failed POST cannot overwrite
 * the withdrawal history with an error payload — the same mistake the web app's
 * agent module had to correct.
 */
export const submitWithdrawal = apiThunk<any, AgencyArg & { amount: number; [k: string]: any }>(
  "payments/submitWithdrawal",
  {
    server: "payment",
    url: ({ agencyId }) => `tenant-agencies/${agencyId}/withdrawals`,
    method: "POST",
    body: ({ agencyId, ...rest }) => rest,
    successMsg: true,
    errorMsg: true,
  }
);

interface PaymentsState {
  balance: any;
  summary: any;
  statement: any[];
  withdrawals: any[];
  errors: any;
  loading: boolean;
  withdrawSubmitting: boolean;
  withdrawSuccess: boolean;
}

const initialState: PaymentsState = {
  balance: null,
  summary: null,
  statement: [],
  withdrawals: [],
  errors: null,
  loading: false,
  withdrawSubmitting: false,
  withdrawSuccess: false,
};

const paymentsSlice = createSlice({
  name: "payments",
  initialState,
  reducers: {
    clearWithdrawStatus: (state) => {
      state.withdrawSuccess = false;
      state.errors = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadBalance.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadBalance.fulfilled, (state, action) => {
        state.balance = action.payload?.data ?? action.payload;
        state.loading = false;
      })
      .addCase(loadBalance.rejected, (state, action) => {
        state.errors = action.payload;
        state.loading = false;
      })

      .addCase(loadEarningsSummary.fulfilled, (state, action) => {
        state.summary = action.payload?.data ?? action.payload;
      })

      .addCase(loadStatement.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadStatement.fulfilled, (state, action) => {
        state.statement = action.payload?.data ?? [];
        state.loading = false;
      })
      .addCase(loadStatement.rejected, (state, action) => {
        state.errors = action.payload;
        state.loading = false;
      })

      .addCase(loadWithdrawals.fulfilled, (state, action) => {
        state.withdrawals = action.payload?.data ?? [];
      })

      .addCase(submitWithdrawal.pending, (state) => {
        state.errors = null;
        state.withdrawSubmitting = true;
        state.withdrawSuccess = false;
      })
      .addCase(submitWithdrawal.fulfilled, (state) => {
        state.withdrawSubmitting = false;
        state.withdrawSuccess = true;
      })
      .addCase(submitWithdrawal.rejected, (state, action) => {
        state.errors = action.payload;
        state.withdrawSubmitting = false;
      });
  },
});

export const { clearWithdrawStatus } = paymentsSlice.actions;
export default paymentsSlice.reducer;
